import React, { useState, useEffect, useContext } from "react";
import { useForm } from "react-hook-form";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import _ from "lodash";
import {
  Box,
  Button,
  Card,
  Grid,
  Stack,
  TextField,
  Typography,
  styled,
} from "@mui/material";
import InsertLinkIcon from "@mui/icons-material/InsertLink";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { AuthenticationContext } from "@cloudmlm/cloudmlm-auth";
import CustomTextField from "../../shared-components/form-controls/customTextField";
import CommonSnackBar from "../../shared-components/snackbar/commonSnackbar";
import SnackState from "../../shared-components/snackbar/snackState";
import { httpGet, httpPost } from "../../services/commonService";
import dataConstant from "../../constants/dataConstants";
import urlConstant from "../../constants/urlConstants";
import { messageConstant } from "../../constants/messageConstant";
import { selectUserDetail, userDetail } from "../../redux/slice/userSlice";
import i18n from "../../i18n";

const replicationSiteNameValidator = (value) => {
  if (!value) {
    return i18n.t(messageConstant.ReplicationSiteNameRequired);
  } else if (!dataConstant.NoSpecialCharacterWithWhiteSpaceRegex.test(value)) {
    return i18n.t(messageConstant.ReplicationSiteNameSpecialCharacter);
  }
  return true;
};

const CustomTextFieldStyle = styled(TextField)(({ theme }) => ({
  "&.MuiFormControl-root": {
    width: "100%",
  },
  "& .MuiOutlinedInput-root": {
    borderRadius: "12px",
    backgroundColor: "var(--inputs)",
    color: "var(--black)",
    fontSize: "0.9rem",
    minHeight: "40px",
    "& .MuiInputBase-input": {
      paddingTop: "15.5px",
      paddingBottom: "15.5px",
      width: "100%",
    },
    "& .MuiInputBase-input:disabled": {
      color: "var(--black)",
      WebkitTextFillColor: "var(--black) !important",
      cursor: "no-drop",
    },
    "& fieldset": {
      border: "none",
    },
    "& Mui-error": {
      position: "absolute",
      left: "0",
    },
  },
  "& .MuiInputLabel-root": {
    position: "static",
    transform: "none",
    fontSize: "0.75rem",
    color: "var(--secondary-text)",
    width: "100%",
  },
}));

const MyWebsite = () => {
  const dispatch = useDispatch();
  const getLoggedUserDetails = useSelector(selectUserDetail);
  const userAcctId = getLoggedUserDetails?.UserAccountDetails?.AcctID;
  const byldDomainName = process.env.REACT_APP_BYLD_DOMAIN_NAME;
  const onCoreDomainName = process.env.REACT_APP_ONCORE_DOMAIN_NAME;
  const reinh2oDomainName = process.env.REACT_APP_REINH2O_DOMAIN_NAME;
  const aventiTravelDomainName = process.env.REACT_APP_AVENTITRAVEL_DOMAIN_NAME;
  const slyceDomainName = process.env.REACT_APP_SLYCE_URL;
  const slyceLockDomainName = process.env.REACT_APP_CONNECT_LOCK_SLYCE_URL;
  const xetaReferralLink = getLoggedUserDetails?.UserAccountDetails?.XetaReferralLink;

  const authenticationState = useContext(AuthenticationContext);
  const { t } = useTranslation();
  const [snack, closeSnack, showSnackbar] = SnackState();

  const [websiteDetails, setWebsiteDetails] = useState({
    replicationSiteName: "",
    isAffiliate: false,
    evmWalletAddress: "",
    walletStatus: null,
  });

  const availableDomains = [
    {
      key: "byld",
      domain: byldDomainName,
      image: `${process.env.REACT_APP_BLOB_URL}/backoffice-assets/website-logos/byld-logo.svg`,
    },
    {
      key: "onCore",
      domain: onCoreDomainName,
      image: `${process.env.REACT_APP_BLOB_URL}/backoffice-assets/website-logos/oncore-logo-white.png`,
    },
    {
      key: "reinh2o",
      domain: reinh2oDomainName,
      image: `${process.env.REACT_APP_BLOB_URL}/backoffice-assets/website-logos/ReinH2O-Logo.png`,
    },
    {
      key: "aventitravel",
      domain: aventiTravelDomainName,
      image: `${process.env.REACT_APP_BLOB_URL}/backoffice-assets/website-logos/AventiLogo.png`,
    },
    {
      key: "xeta",
      domain: "",
      image: `${process.env.REACT_APP_BLOB_URL}/backoffice-assets/website-logos/Xeta_Logo_White.svg`,
    },
    {
      key: "slyce",
      domain: slyceDomainName,
      image: `${process.env.REACT_APP_BLOB_URL}/backoffice-assets/website-logos/slyce-logo-white.png`,
    },
    {
      key: "slyceLock",
      domain: slyceLockDomainName,
      image: `${process.env.REACT_APP_BLOB_URL}/backoffice-assets/website-logos/slyce-logo-white.png`,
    },
  ];

  const methods = useForm({
    defaultValues: {
      ReplicationSiteName: "",
    },
    mode: "all",
  });

  const { getValues } = methods;

  const formConfig = {
    replicationSiteNameConfig: {
      name: "ReplicationSiteName",
      //required: true,
      fullWidth: true,
      variant: "outlined",
      className: "cm-sitename",
      type: "text",
      maxLength: 100,
      validation: {
        validate: replicationSiteNameValidator,
      },
    },
  };

  const copyToClipboard = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      showSnackbar(t("Copied to clipboard"), "success");
    } catch (err) {}
  };

  const getWebsiteDetailsById = async () => {
    setWebsiteDetails({
      replicationSiteName:
        getLoggedUserDetails?.UserAccountDetails?.ReplicationSiteName,
      isAffiliate:
        getLoggedUserDetails?.PurchasePackageDetails?.LatestPackageType?.toLowerCase() ===
        "affiliate"
          ? true
          : false,
      evmWalletAddress: "",
      walletStatus: null,
    });

    methods.setValue(
      "ReplicationSiteName",
      getLoggedUserDetails?.UserAccountDetails?.ReplicationSiteName
    );

    if (userAcctId) {
      try {
        const response = await httpGet(
          `${urlConstant.PublicWalletAddress.GetPublicWalletAddress}?AcctId=${userAcctId}`,
          true,
          true,
          authenticationState.accessToken,
          authenticationState.impersonateEmail,
        );
        if ( response?.Value?.Status === dataConstant.APIResponseStatus.Success) {
          const walletData = response.Value.Data;
          if (walletData?.WalletAddress) {
            setWebsiteDetails((prev) => ({
              ...prev,
              evmWalletAddress: walletData.WalletAddress,
              walletStatus: walletData.WalletStatus || null,
            }));
          }
        }
      } catch (err) {
        console.error("Failed to fetch wallet address:", err);
      }
    }
  };

  useEffect(() => {
    if (
      authenticationState &&
      authenticationState.isLoggedIn &&
      getLoggedUserDetails?.UserAccountDetails?.ReplicationSiteName
    ) {
      getWebsiteDetailsById();
    }
  }, [getLoggedUserDetails]);

  const onSubmit = (data) => {
    const model = {
      ReplicationSiteName: data.ReplicationSiteName,
    };

    httpPost(
      urlConstant.WebsiteName.UpdateWebsiteName,
      model,
      true,
      true,
      authenticationState.accessToken,
      authenticationState.impersonateEmail
    )
      .then((response) => {
        if (
          response &&
          response.Status === dataConstant.APIResponseStatus.Success
        ) {
          showSnackbar(response.description, "success");
          const updatedUserDetails = {
            ...getLoggedUserDetails,
            UserAccountDetails: {
              ...getLoggedUserDetails.UserAccountDetails,
              ReplicationSiteName: data.ReplicationSiteName,
            },
          };
          dispatch(userDetail({ userDetail: updatedUserDetails }));
        }
      })
      .catch((err) => {
        console.error(err);
      });
  };

  const handleUpdateClick = () => {
    methods.handleSubmit(onSubmit)();
  };

  const renderDomainCard = (domainInfo) => {
    let fullUrl = `https://${websiteDetails.replicationSiteName}${domainInfo.domain}`;
    let displayCard = "block";
    const bothWalletsVerified = websiteDetails.evmWalletAddress && websiteDetails.walletStatus === "Verified";
    if (domainInfo?.key === "byld" && websiteDetails.isAffiliate) {
      displayCard = "none";
    } else if (domainInfo?.key === "xeta") {
      fullUrl = xetaReferralLink;
      if (fullUrl === "" || fullUrl === null) {
        displayCard = "none";
      }
    } else if (domainInfo?.key === "slyce") {
      fullUrl = `${slyceDomainName}?affiliate=${websiteDetails.evmWalletAddress}`;
      if (!bothWalletsVerified) {
        displayCard = "none";
      }
    } else if (domainInfo?.key === "slyceLock") {
      fullUrl = `${slyceLockDomainName}?affiliate=${websiteDetails.evmWalletAddress}`;
      if (!bothWalletsVerified) {
        displayCard = "none";
      }
    }
    return (
      <Grid
        size={{ xs: 12, md: 6 }}
        key={domainInfo.key}
        sx={{ display: displayCard }}
      >
        <Card
          sx={(theme) => ({
            p: {
              xs: "20px",
              sm: "25px 30px",
            },
            background: theme.palette.secondary.lightest,
            borderRadius: "20px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            width: "100%",
            gap: 2,
          })}
        >
          <img
            width={213}
            className="sitelinks-image"
            height={"auto"}
            src={domainInfo.image}
          />
          <Stack
            className="customfield-label"
            sx={{
              width: "100%",
              flexDirection: "row",
              alignItems: "flex-end",
            }}
          >
            {domainInfo.key === "xeta" && (
              <CustomTextFieldStyle
                value={(xetaReferralLink || "").replace(/^https?:\/\//, "")}
              />
            )}
            {domainInfo.key !== "xeta" && (
              <>
                {domainInfo.key === "slyce" || domainInfo.key === "slyceLock" ? (
                  <>
                   <Card className="customfield-label-inner-left">
                      {domainInfo.key === "slyce" ?  t("WebsiteName.SlyceWebsite") : t("WebsiteName.SlyceLock")}
                   </Card>
                  <CustomTextFieldStyle
                    value={fullUrl?.replace(/^https?:\/\//, "")}
                    disabled
                    className="customfield-label-inner-text-field"
                  />
                  </>
                ) : (
                  <>
                    <CustomTextFieldStyle
                      value={getValues("ReplicationSiteName")}
                    />
                    <Card className="customfield-label-inner">
                      {domainInfo.domain}
                    </Card>
                  </>
                )}
              </>
            )}
          </Stack>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            gap={1}
            width={"100%"}
            alignItems={"center"}
            justifyContent={"space-between"}
          >
            <Button
              variant="outlined"
              color="secondary"
              startIcon={<InsertLinkIcon sx={{ mr: 0 }} />}
              sx={{
                width: { xs: "100%", sm: "auto" },
                px: 3,
              }}
              component="a"
              href={fullUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              {t("Common.Visit Link")}
            </Button>
            <Button
              className="text-white-button"
              disableElevation
              variant="contained"
              color="secondary"
              startIcon={<ContentCopyIcon />}
              sx={{
                width: { xs: "100%", sm: "auto" },
                px: 3,
              }}
              onClick={() => copyToClipboard(fullUrl)}
            >
              {t("Common.Copy")}
            </Button>
          </Stack>
        </Card>
      </Grid>
    );
  };

  return (
    <>
      <Box flexDirection={"column"} gap={2} display={"flex"}>
        <Typography color="secondary.light" variant="h4" fontWeight={600}>
          {t("My Account")}
        </Typography>

        <Box width={"100%"}>
          <Card
            sx={(theme) => ({
              p: {
                xs: "20px",
                sm: "25px 30px",
              },
              background: theme.palette.background.cardGradient,
              borderRadius: "20px",
              height: "100%",
            })}
          >
            <Stack gap={"20px"}>
              <Typography color="secondary.light" variant="h4" fontWeight={600}>
                {t("WebsiteName.Website Name")}
              </Typography>
              <Card
                sx={(theme) => ({
                  p: "20px",
                  background: theme.palette.primary.lightdark,
                  borderRadius: "20px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "20px",
                  flexDirection: "column",
                })}
              >
                <Card
                  sx={(theme) => ({
                    p: {
                      xs: "20px",
                      sm: "25px 30px",
                    },
                    background: theme.palette.background.cardGradient,
                    borderRadius: "20px",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    width: "100%",
                    gap: 1,
                  })}
                >
                  <Typography
                    sx={(theme) => ({
                      mt: 1,
                      color: theme.palette.common.white,
                      "& a": {
                        color: "var(--secondary-text)",
                        textDecoration: "underline",
                      },
                    })}
                    variant="h5"
                  >
                    {t("WebsiteName.Unique url text1")}
                    <Box
                      component="span"
                      sx={{
                        color: "secondary.text",
                        fontWeight: "600",
                        wordBreak: "break-word",
                      }}
                    >
                      {t("WebsiteName.Unique url text2").replace(
                        "{0}",
                        `${byldDomainName}`
                      )}
                    </Box>
                  </Typography>
                </Card>
                <Stack>
                  <Typography
                    sx={(theme) => ({
                      color: theme.palette.secondary.text,
                      fontWeight: "600",
                      textAlign: { xs: "center", sm: "left" },
                    })}
                    variant="body1"
                  >
                    {t("WebsiteName.Unique url text3")}
                  </Typography>
                </Stack>
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  gap={1.5}
                  width={"100%"}
                  alignItems={{ xs: "flex-start", sm: "flex-start" }}
                  justifyContent={"center"}
                >
                  <Stack
                    className="customfield-label"
                    sx={{
                      maxWidth: "700px",
                      width: "100%",
                      flexDirection: "row",
                      alignItems: "flex-start",
                    }}
                  >
                    <CustomTextField
                      methods={methods}
                      properties={formConfig.replicationSiteNameConfig}
                    />
                    <Card
                      sx={{
                        display: {
                          xs: "none !important",
                          sm: "flex !important",
                        },
                      }}
                      className="customfield-label-inner"
                    >
                      {byldDomainName}
                    </Card>
                  </Stack>
                  <Button
                    variant="contained"
                    sx={{ px: 3, py: 1.6, width: { xs: "100%", sm: "auto" } }}
                    onClick={handleUpdateClick}
                  >
                    {t("Common.Update")}
                  </Button>
                </Stack>
                {websiteDetails.replicationSiteName && (
                  <>
                    <Typography
                      sx={(theme) => ({
                        color: theme.palette.common.white,
                        width: "100%",
                        textAlign: { xs: "center", sm: "left" },
                      })}
                      variant="h5"
                    >
                      {t("WebsiteName.YourReplicatedSiteLinks")}
                    </Typography>
                    <Grid
                      container
                      spacing={3}
                      sx={{ flexGrow: 1, width: "100%" }}
                    >
                      {_.map(availableDomains, renderDomainCard)}
                    </Grid>
                  </>
                )}
              </Card>
            </Stack>
          </Card>
        </Box>
      </Box>
      <CommonSnackBar snackObj={snack} closeSnack={closeSnack} buttonProp />
    </>
  );
};

export default MyWebsite;